import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Users, HeartPulse, Brain, Apple, Baby, Cigarette, User2, TrendingUp, Activity } from 'lucide-react';
import { careLinesApi } from '@/api/endpoints/carelines.api';
import type { CareLineListResponse } from '@/types/careline.types';

// Icon mapping for care line codes
const careLineIcons: Record<string, React.ElementType> = {
  MENTAL_HEALTH: Brain,
  CHRONIC_METABOLIC: HeartPulse,
  OBESITY: Apple,
  FRAIL_ELDERLY: User2,
  PRENATAL: Baby,
  SMOKING_CESSATION: Cigarette,
};

// Color mapping for care line codes
const careLineColors: Record<string, string> = {
  MENTAL_HEALTH: 'bg-purple-500',
  CHRONIC_METABOLIC: 'bg-blue-500',
  OBESITY: 'bg-orange-500',
  FRAIL_ELDERLY: 'bg-gray-500',
  PRENATAL: 'bg-pink-500',
  SMOKING_CESSATION: 'bg-amber-500',
};

export default function CareLinesPage() {
  const navigate = useNavigate();

  // Query for care lines (cached)
  const {
    data: careLines = [],
    isLoading: loadingCareLines,
    error: errorCareLines,
    refetch: refetchCareLines,
  } = useQuery({
    queryKey: ['care-lines', 'active'],
    queryFn: () => careLinesApi.getAllActive(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Query for statistics (cached)
  const {
    data: statistics,
    isLoading: loadingStats,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ['care-lines', 'statistics'],
    queryFn: () => careLinesApi.getStatistics(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const loading = loadingCareLines || loadingStats;
  const error = errorCareLines;

  const refetch = () => {
    refetchCareLines();
    refetchStats();
  };

  const totalPatients = careLines.reduce((sum, cl) => sum + (cl.activePatientCount || 0), 0);

  if (loading && careLines.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Carregando linhas de cuidado...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-800">Erro ao carregar linhas de cuidado. Tente novamente.</p>
        <button
          onClick={() => refetch()}
          className="mt-2 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Linhas de Cuidado</h1>
          <p className="text-muted-foreground">
            {careLines.length} linha{careLines.length !== 1 ? 's' : ''} ativa{careLines.length !== 1 ? 's' : ''}
            {' '}&bull; {totalPatients} paciente{totalPatients !== 1 ? 's' : ''} inscrito{totalPatients !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 rounded-lg border px-4 py-2 hover:bg-muted"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total de Pacientes Ativos</p>
                <p className="text-2xl font-bold">{statistics.totalActivePatients}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-100 p-2 dark:bg-green-900/30">
                <Activity className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Linhas Ativas</p>
                <p className="text-2xl font-bold">{careLines.length}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/30">
                <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Média por Linha</p>
                <p className="text-2xl font-bold">
                  {careLines.length > 0
                    ? Math.round(statistics.totalActivePatients / careLines.length)
                    : 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {careLines.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
          <HeartPulse className="h-12 w-12 mb-2" />
          <p>Nenhuma linha de cuidado ativa</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {careLines.map((careLine) => (
            <CareLineCard
              key={careLine.id}
              careLine={careLine}
              onClick={() => navigate(`/care-lines/${careLine.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CareLineCardProps {
  careLine: CareLineListResponse;
  onClick: () => void;
}

function CareLineCard({ careLine, onClick }: CareLineCardProps) {
  const Icon = careLineIcons[careLine.code] || HeartPulse;
  const colorClass = careLineColors[careLine.code] || 'bg-primary';

  return (
    <div
      onClick={onClick}
      className="cursor-pointer rounded-lg border bg-card p-6 transition-shadow hover:shadow-md"
    >
      <div className="mb-4 flex items-center gap-3">
        <div className={`rounded-lg p-2 ${colorClass} text-white`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className={`h-2 flex-1 rounded ${colorClass} opacity-20`} />
      </div>

      <h3 className="text-lg font-semibold">{careLine.name}</h3>

      {careLine.description && (
        <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
          {careLine.description}
        </p>
      )}

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="text-2xl font-bold">{careLine.activePatientCount || 0}</span>
          <span className="text-sm text-muted-foreground">pacientes</span>
        </div>
      </div>

      {careLine.monitoringFrequencyDays && (
        <p className="mt-2 text-xs text-muted-foreground">
          Monitoramento a cada {careLine.monitoringFrequencyDays} dias
        </p>
      )}

      <div className="mt-4 w-full rounded-lg border py-2 text-center text-sm font-medium hover:bg-muted transition-colors">
        Ver detalhes
      </div>
    </div>
  );
}
